import { useCallback, useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useOffice } from '@/hooks/useOffice';
import {
  BackupOperationError,
  type FullSnapshot,
  type OfficeSnapshot,
  downloadBackup,
  runBackupOperation,
} from '@/lib/backupOperations';
import { OFFICE_BACKUP_TAPE_COLUMNS, parseCsv, rowsToCsv } from './format';

interface Counts { tapes: number; boxes: number; }
type Message = { type: 'success' | 'error'; text: string } | null;
type ClearTarget = { scope: 'office' | 'all_offices'; officeId: number | null; officeName: string };
interface ClearChallenge { challenge_id: string; question: string; expires_in_seconds: number; }

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function buildOfficeBackupFileName(officeName?: string | null): string {
  const date = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}_${p(date.getHours())}-${p(date.getMinutes())}-${p(date.getSeconds())}`;
  return `TAPEVAULT_${(officeName || 'Office').trim() || 'Office'}_Backup_${stamp}.zip`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('zh-CN');
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(2)} MB`;
}

function errorMessage(error: unknown, fallback = '备份操作失败'): string {
  if (error instanceof BackupOperationError) {
    if (error.status === 401) return '登录状态已失效，请重新登录';
    if (error.status === 403) return '当前账号没有执行此操作的权限';
    if (error.status === 409) return '数据在操作期间发生变化，请刷新列表后重试';
    if (error.status === 413) return '备份数据超过当前操作限制，请拆分备份后重试';
    if (error.code.startsWith('CLEAR_CHALLENGE_')) return error.message;
    if (error.status === 422) return '备份文件校验失败，无法恢复';
    if (error.status === 503) return 'Cloudflare 备份存储暂时不可用，请稍后重试';
    return error.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function isOwner(profile: { is_owner?: boolean } | null): boolean {
  return profile?.is_owner === true || Number(profile?.is_owner) === 1;
}

function ConfirmButton({ label, title, disabled, onConfirm }: { label: string; title: string; disabled?: boolean; onConfirm: () => Promise<void> | void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => { if (window.confirm(title)) void onConfirm(); }}
      className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
    >
      {label}
    </button>
  );
}

function ClearDataDialog({
  target, disabled, onClose, onConfirm,
}: {
  target: ClearTarget;
  disabled: boolean;
  onClose: () => void;
  onConfirm: (challengeId: string, answer: number) => Promise<void>;
}) {
  const [challenge, setChallenge] = useState<ClearChallenge | null>(null);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const isGlobal = target.scope === 'all_offices';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setChallenge(null);
    setAnswer('');
    setError('');
    const action = isGlobal ? 'all_offices_clear_challenge' : 'office_clear_challenge';
    const payload = target.officeId === null ? {} : { office_id: target.officeId };
    void runBackupOperation<ClearChallenge>(action, payload).then((result) => {
      if (active) setChallenge(result);
    }).catch((cause) => {
      if (active) setError(errorMessage(cause, '无法获取验证题'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [isGlobal, refreshKey, target.officeId]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!challenge || !/^\d+$/.test(answer)) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(challenge.challenge_id, Number(answer));
    } catch (cause) {
      setError(errorMessage(cause, '验证失败，请重试'));
    } finally {
      setSubmitting(false);
    }
  };

  const title = isGlobal ? '清除所有办公室磁带记录' : `清除${target.officeName}的磁带记录`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="clear-data-title" className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="clear-data-title" className="text-base font-semibold text-slate-900">{title}</h2>
            <p className="mt-1 text-xs text-slate-500">此操作不可撤销，只删除磁带记录。请完成算术验证。</p>
          </div>
          <button type="button" aria-label="关闭" title="关闭" onClick={onClose} className="text-slate-400 hover:text-slate-700 cursor-pointer" disabled={submitting}>
            <i className="ri-close-line h-5 w-5 flex items-center justify-center" />
          </button>
        </div>
        <form onSubmit={(event) => void submit(event)} className="mt-5 space-y-4">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-5 text-center">
            {loading ? <span className="text-sm text-slate-500">正在获取验证题…</span>
              : challenge ? <span className="font-mono text-2xl font-semibold text-slate-900">{challenge.question} = ?</span>
                : <span className="text-sm text-red-700">{error || '无法获取验证题'}</span>}
          </div>
          {challenge && <label className="block text-sm text-slate-700">
            验证答案
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              placeholder="输入计算结果"
              aria-label="验证答案"
              disabled={submitting || disabled}
            />
          </label>}
          {error && challenge && <p role="alert" className="text-xs text-red-700">{error}</p>}
          <div className="flex flex-wrap justify-between gap-2">
            <button type="button" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading || submitting || disabled} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900 cursor-pointer disabled:opacity-50">重新获取题目</button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} disabled={submitting || disabled} className="px-3 py-2 text-xs border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer disabled:opacity-50">取消</button>
              <button type="submit" disabled={!challenge || loading || submitting || disabled || !/^\d+$/.test(answer)} className="px-3 py-2 text-xs rounded-md bg-red-700 text-white hover:bg-red-800 cursor-pointer disabled:opacity-50">{submitting ? '正在清除…' : '验证并清除'}</button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

export default function BackupPage() {
  const { profile } = useAuth();
  const { currentOfficeId, currentOffice } = useOffice();
  const owner = isOwner(profile);
  const canClear = owner || profile?.role === 'admin';
  const [counts, setCounts] = useState<Counts>({ tapes: 0, boxes: 0 });
  const [officeSnapshots, setOfficeSnapshots] = useState<OfficeSnapshot[]>([]);
  const [fullSnapshots, setFullSnapshots] = useState<FullSnapshot[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [working, setWorking] = useState('');
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState('');
  const [msg, setMsg] = useState<Message>(null);
  const [clearTarget, setClearTarget] = useState<ClearTarget | null>(null);
  const restoreInputRef = useRef<HTMLInputElement>(null);

  const loadCounts = useCallback(async () => {
    if (!currentOfficeId) return;
    try {
      const [tapes, rows] = await Promise.all([
        supabase.from('tapes').select('id', { count: 'exact', head: true }).eq('office_id', currentOfficeId),
        supabase.from('tapes').select('box_number').eq('office_id', currentOfficeId).not('box_number', 'is', null),
      ]);
      const boxes = new Set<string>();
      ((rows.data as { box_number: string | null }[]) || []).forEach(({ box_number }) => {
        const value = (box_number || '').trim();
        if (value && value !== 'Backup Tape Library') boxes.add(value);
      });
      setCounts({ tapes: tapes.count ?? 0, boxes: boxes.size });
    } catch { /* Optional counters must not block backup operations. */ }
  }, [currentOfficeId]);

  const loadSnapshots = useCallback(async () => {
    if (!currentOfficeId) { setOfficeSnapshots([]); return; }
    setLoadingSnapshots(true);
    try {
      const result = await runBackupOperation<{ snapshots: OfficeSnapshot[] }>('office_list', { office_id: currentOfficeId, limit: 100 });
      setOfficeSnapshots(result.snapshots || []);
    } catch (error) {
      setMsg({ type: 'error', text: errorMessage(error, '无法加载办公室在线备份') });
    } finally { setLoadingSnapshots(false); }
  }, [currentOfficeId]);

  const loadFullSnapshots = useCallback(async () => {
    if (!owner) { setFullSnapshots([]); return; }
    try {
      const result = await runBackupOperation<{ snapshots: FullSnapshot[] }>('full_list', { limit: 100 });
      setFullSnapshots(result.snapshots || []);
    } catch (error) {
      setMsg({ type: 'error', text: errorMessage(error, '无法加载 D1 完整镜像列表') });
    }
  }, [owner]);

  useEffect(() => { setCounts({ tapes: 0, boxes: 0 }); void loadCounts(); }, [loadCounts]);
  useEffect(() => { void loadSnapshots(); }, [loadSnapshots]);
  useEffect(() => { void loadFullSnapshots(); }, [loadFullSnapshots]);

  const run = async (key: string, operation: () => Promise<void>, success?: string) => {
    setWorking(key); setMsg(null);
    try { await operation(); if (success) setMsg({ type: 'success', text: success }); }
    catch (error) { setMsg({ type: 'error', text: errorMessage(error) }); }
    finally { setWorking(''); }
  };

  const createOfficeSnapshot = () => run('office-create', async () => {
    if (!currentOfficeId) return;
    await runBackupOperation('office_create', { office_id: currentOfficeId });
    await loadSnapshots();
  }, '办公室在线备份已创建');

  const restoreOfficeSnapshot = (snapshot: OfficeSnapshot) => run(`office-restore-${snapshot.snapshot_id}`, async () => {
    if (!currentOfficeId) return;
    await runBackupOperation('office_restore', { office_id: currentOfficeId, snapshot_id: snapshot.snapshot_id });
    await Promise.all([loadSnapshots(), loadCounts()]);
  }, '办公室数据已恢复，页面数据已刷新');

  const verifyOfficeSnapshot = (snapshot: OfficeSnapshot) => run(`office-verify-${snapshot.snapshot_id}`, async () => {
    await runBackupOperation('office_verify', { office_id: snapshot.office_id, snapshot_id: snapshot.snapshot_id });
  }, '办公室备份校验通过');

  const confirmClear = async (challengeId: string, answer: number) => {
    if (!clearTarget) return;
    const target = clearTarget;
    const global = target.scope === 'all_offices';
    const action = global ? 'all_offices_clear' : 'office_clear';
    setWorking(global ? 'all-offices-clear' : 'office-clear');
    setMsg(null);
    try {
      const payload = target.officeId === null ? {} : { office_id: target.officeId };
      const result = await runBackupOperation<{ deleted_count: number }>(action, {
        ...payload, challenge_id: challengeId, answer,
      });
      await loadCounts();
      setClearTarget(null);
      setMsg({ type: 'success', text: global
        ? `已清除所有办公室的 ${result.deleted_count} 条磁带记录`
        : `已清除当前办公室的 ${result.deleted_count} 条磁带记录` });
    } catch (error) {
      setMsg({ type: 'error', text: errorMessage(error) });
      throw error;
    } finally { setWorking(''); }
  };

  const createFullSnapshot = () => run('full-create', async () => {
    await runBackupOperation('full_create');
    await loadFullSnapshots();
  }, 'D1 完整镜像已创建并保存到私有 R2');

  const restoreFullSnapshot = (snapshot: FullSnapshot) => run(`full-restore-${snapshot.snapshot_id}`, async () => {
    await runBackupOperation('full_restore', { snapshot_id: snapshot.snapshot_id });
    await Promise.all([loadFullSnapshots(), loadCounts()]);
  }, 'D1 完整镜像已恢复。账号资料已回到快照时点，请重新登录');

  const verifyFullSnapshot = (snapshot: FullSnapshot) => run(`full-verify-${snapshot.snapshot_id}`, async () => {
    await runBackupOperation('full_verify', { snapshot_id: snapshot.snapshot_id });
  }, 'D1 完整镜像校验通过');

  const downloadOffice = (snapshot: OfficeSnapshot) => run(`office-download-${snapshot.snapshot_id}`, () => downloadBackup('office_download', { office_id: snapshot.office_id, snapshot_id: snapshot.snapshot_id }, `tapevault-office-${snapshot.office_id}-${snapshot.snapshot_id}.json`));
  const downloadFull = (snapshot: FullSnapshot) => run(`full-download-${snapshot.snapshot_id}`, () => downloadBackup('full_download', { snapshot_id: snapshot.snapshot_id }, `tapevault-full-${snapshot.snapshot_id}.json`));

  const backup = async () => {
    if (!currentOfficeId) return;
    setBackingUp(true); setMsg(null);
    try {
      const { data: tapes, error } = await supabase.from('tapes').select('*').eq('office_id', currentOfficeId).order('id', { ascending: true });
      if (error) throw error;
      const zip = new JSZip();
      zip.file('tapes.csv', rowsToCsv(OFFICE_BACKUP_TAPE_COLUMNS, (tapes || []).map((row) => {
        const result: Record<string, unknown> = {};
        OFFICE_BACKUP_TAPE_COLUMNS.forEach((field) => { result[field] = (row as Record<string, unknown>)[field] ?? ''; });
        return result;
      })));
      downloadBlob(await zip.generateAsync({ type: 'blob' }), buildOfficeBackupFileName(currentOffice?.name));
      setMsg({ type: 'success', text: 'ZIP/CSV 备份已下载' });
    } catch (error) { setMsg({ type: 'error', text: errorMessage(error, 'ZIP/CSV 备份失败') }); }
    finally { setBackingUp(false); }
  };

  const onPickRestore = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentOfficeId) return;
    if (!window.confirm(`确定要把 ZIP/CSV 数据导入到“${currentOffice?.name || '当前办公室'}”吗？现有数据不会自动清除。`)) return;
    setRestoring(true); setMsg(null);
    try {
      const zip = await JSZip.loadAsync(file);
      const entry = zip.file('tapes.csv');
      const tapes = entry ? parseCsv(await entry.async('string')) : [];
      if (!tapes.length) throw new Error('备份 ZIP 中没有可导入的 tapes.csv 数据');
      for (let i = 0; i < tapes.length; i += 50) {
        const rows = tapes.slice(i, i + 50).map((row) => ({
          ...row,
          needs_replacement: row.needs_replacement === 'true',
          damaged: row.damaged === 'true',
          is_new: ['true', '1', 't', 'yes'].includes(String(row.is_new ?? '').trim().toLowerCase()),
          office_id: currentOfficeId,
        }));
        const { error } = await supabase.from('tapes').insert(rows);
        if (error) throw new Error(`导入第 ${Math.floor(i / 50) + 1} 批失败：${error.message}`);
      }
      setRestoreFile(file.name);
      await loadCounts();
      setMsg({ type: 'success', text: `ZIP/CSV 导入完成，共导入 ${tapes.length} 条磁带` });
    } catch (error) { setMsg({ type: 'error', text: errorMessage(error, 'ZIP/CSV 导入失败') }); }
    finally { setRestoring(false); }
  };

  return (
    <div className="p-8 space-y-6">
      <div><h1 className="text-2xl font-semibold">备份与恢复</h1><p className="text-sm text-slate-500 mt-1">保留原 ZIP/CSV 离线方式，同时使用 Cloudflare 私有 R2 保存可校验的 D1 快照。</p></div>
      {msg && <div className={`p-3 rounded-md text-sm border flex items-center justify-between ${msg.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}><span>{msg.text}</span><button type="button" onClick={() => setMsg(null)} className="text-xs underline cursor-pointer">关闭</button></div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg border border-slate-200 p-5"><div className="flex items-center gap-2 mb-1"><i className="ri-download-cloud-2-line w-5 h-5 flex items-center justify-center text-slate-500" /><h2 className="font-semibold text-sm">ZIP/CSV 离线备份</h2></div><p className="text-xs text-slate-500 mb-4">兼容原有备份文件格式，可在办公室数据导入错误时手工处理。</p><div className="grid grid-cols-2 gap-3 mb-4"><div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-md"><span className="text-xs text-slate-500">磁带</span><span className="text-sm font-semibold">{counts.tapes}</span></div><div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-md"><span className="text-xs text-slate-500">盒子</span><span className="text-sm font-semibold">{counts.boxes}</span></div></div><button type="button" onClick={backup} disabled={backingUp || !currentOfficeId} className="w-full px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 cursor-pointer inline-flex items-center justify-center gap-2"><i className={`${backingUp ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} w-4 h-4 flex items-center justify-center`} />{backingUp ? '正在生成…' : '下载 ZIP/CSV 备份'}</button></section>

        <section className="bg-white rounded-lg border border-slate-200 p-5"><div className="flex items-center gap-2 mb-1"><i className="ri-upload-cloud-2-line w-5 h-5 flex items-center justify-center text-slate-500" /><h2 className="font-semibold text-sm">ZIP/CSV 导入</h2></div><p className="text-xs text-slate-500 mb-4">导入会追加到当前办公室，不会自动清除现有数据；需要清空时使用下方按钮。</p><button type="button" onClick={() => restoreInputRef.current?.click()} disabled={restoring || !currentOfficeId} className="w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition border-slate-200 hover:border-slate-400 disabled:opacity-50"><i className="ri-file-zip-line w-8 h-8 flex items-center justify-center mx-auto text-slate-400 text-2xl" /><div className="text-sm font-medium mt-3">选择 ZIP 备份文件</div><div className="text-xs text-slate-500 mt-1">支持原有 tapes.csv 格式</div></button>{restoreFile && <div className="mt-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 truncate">最近导入：{restoreFile}</div>}<input ref={restoreInputRef} type="file" accept=".zip,application/zip" onChange={onPickRestore} className="hidden" /></section>
      </div>

      <section className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <div className="flex items-center gap-2"><i className="ri-cloud-line w-5 h-5 flex items-center justify-center text-slate-500" /><h2 className="font-semibold text-sm">办公室在线备份</h2></div>
            <p className="text-xs text-slate-500 mt-1">当前办公室：{currentOffice?.name || '—'}。User 只能操作已授权办公室。</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => void loadSnapshots()} disabled={loadingSnapshots} className="px-3 py-1.5 text-xs border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer disabled:opacity-50"><i className="ri-refresh-line mr-1" />刷新</button>
            <button type="button" onClick={createOfficeSnapshot} disabled={!!working || !currentOfficeId} className="px-3 py-1.5 text-xs bg-slate-900 text-white rounded-md hover:bg-slate-800 cursor-pointer disabled:opacity-50"><i className="ri-add-line mr-1" />创建办公室快照</button>
          </div>
        </div>
        {owner && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-red-800">将清除所有办公室的磁带记录，不影响办公室、账号权限、日志及 R2 备份。</span>
          <button type="button" onClick={() => setClearTarget({ scope: 'all_offices', officeId: null, officeName: '所有办公室' })} disabled={!!working} className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 rounded-md hover:bg-red-100 cursor-pointer disabled:opacity-50">清除所有办公室磁带</button>
        </div>}
        {canClear && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-amber-800">清除会删除当前办公室的全部磁带记录，不能替代备份。</span>
          <button type="button" onClick={() => currentOfficeId && setClearTarget({ scope: 'office', officeId: currentOfficeId, officeName: currentOffice?.name || '当前办公室' })} disabled={!!working || !currentOfficeId} className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-700 rounded-md hover:bg-red-50 cursor-pointer disabled:opacity-40">清除当前办公室数据</button>
        </div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100"><th className="py-2 pr-3 font-medium">创建时间</th><th className="py-2 pr-3 font-medium">磁带数</th><th className="py-2 pr-3 font-medium">类型</th><th className="py-2 font-medium">操作</th></tr></thead>
            <tbody>
              {!loadingSnapshots && officeSnapshots.map((snapshot) => <tr key={snapshot.snapshot_id} className="border-t border-slate-100">
                <td className="py-3 pr-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(snapshot.created_at)}</td><td className="py-3 pr-3 text-xs">{snapshot.tape_count}</td><td className="py-3 pr-3 text-xs">{snapshot.purpose === 'pre_restore' ? '恢复前自动快照' : '手动快照'}</td>
                <td className="py-3"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => void verifyOfficeSnapshot(snapshot)} disabled={!!working} className="text-xs text-slate-600 underline cursor-pointer disabled:opacity-40">校验</button><button type="button" onClick={() => void downloadOffice(snapshot)} disabled={!!working} className="text-xs text-slate-600 underline cursor-pointer disabled:opacity-40">下载</button><ConfirmButton label="恢复" title={`确定将当前办公室恢复到 ${formatDate(snapshot.created_at)} 的快照吗？当前磁带数据会被替换。`} disabled={!!working} onConfirm={() => restoreOfficeSnapshot(snapshot)} /></div></td>
              </tr>)}
              {!loadingSnapshots && officeSnapshots.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-xs text-slate-400">暂无在线办公室快照</td></tr>}
              {loadingSnapshots && <tr><td colSpan={4} className="py-8 text-center text-xs text-slate-400"><i className="ri-loader-4-line animate-spin mr-1" />正在加载…</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {owner && <section className="bg-white rounded-lg border border-slate-200 p-5"><div className="flex flex-wrap items-center justify-between gap-3 mb-2"><div><div className="flex items-center gap-2"><i className="ri-database-2-line w-5 h-5 flex items-center justify-center text-slate-500" /><h2 className="font-semibold text-sm">Owner：D1 完整镜像</h2></div><p className="text-xs text-slate-500 mt-1">包含账号、办公室、磁带和日志等受保护表。恢复后所有账号资料回到快照时点，现有会话会失效。</p></div><div className="flex gap-2"><button type="button" onClick={() => void loadFullSnapshots()} className="px-3 py-1.5 text-xs border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer"><i className="ri-refresh-line mr-1" />刷新</button><button type="button" onClick={createFullSnapshot} disabled={!!working} className="px-3 py-1.5 text-xs bg-slate-900 text-white rounded-md hover:bg-slate-800 cursor-pointer disabled:opacity-50"><i className="ri-save-3-line mr-1" />创建完整镜像</button></div></div><div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100"><th className="py-2 pr-3 font-medium">创建时间</th><th className="py-2 pr-3 font-medium">类型</th><th className="py-2 pr-3 font-medium">表记录数</th><th className="py-2 pr-3 font-medium">大小</th><th className="py-2 font-medium">操作</th></tr></thead><tbody>{fullSnapshots.map((snapshot) => <tr key={snapshot.snapshot_id} className="border-t border-slate-100"><td className="py-3 pr-3 text-xs text-slate-600 whitespace-nowrap">{formatDate(snapshot.created_at)}</td><td className="py-3 pr-3 text-xs whitespace-nowrap">{snapshot.purpose === 'pre_restore' ? '恢复前自动镜像' : '手动镜像'}</td><td className="py-3 pr-3 text-xs">{Object.values(snapshot.row_counts).reduce((sum, count) => sum + count, 0)}</td><td className="py-3 pr-3 text-xs">{formatBytes(snapshot.size_bytes)}</td><td className="py-3"><div className="flex flex-wrap gap-2"><button type="button" onClick={() => void verifyFullSnapshot(snapshot)} disabled={!!working} className="text-xs text-slate-600 underline cursor-pointer disabled:opacity-40">校验</button><button type="button" onClick={() => void downloadFull(snapshot)} disabled={!!working} className="text-xs text-slate-600 underline cursor-pointer disabled:opacity-40">下载镜像</button>{snapshot.purpose === 'manual' && <ConfirmButton label="恢复 D1" title={`确定将整个 D1 恢复到 ${formatDate(snapshot.created_at)} 吗？所有业务数据和账号资料会被替换，完成后必须重新登录。`} disabled={!!working} onConfirm={() => restoreFullSnapshot(snapshot)} />}</div></td></tr>)}{!fullSnapshots.length && <tr><td colSpan={5} className="py-8 text-center text-xs text-slate-400">暂无 D1 完整镜像</td></tr>}</tbody></table></div></section>}
      {clearTarget && <ClearDataDialog target={clearTarget} disabled={!!working} onClose={() => setClearTarget(null)} onConfirm={confirmClear} />}
    </div>
  );
}
